import { type RefObject } from 'react';

import {
  type EmbeddedPaymentElementConfiguration,
  type IntentConfiguration,
} from '@stripe/stripe-react-native';

import { type BagReward } from '@sg/shared-fe-features-loyalty';

import {
  type OrderingCart,
  type OrderingCustomer,
  type PaymentMethod,
  type UpsellItem,
  type UpsellPlacement,
  type WantedTime,
} from './ordering-machine.types';

export {
  type BagReward,
  type RedemptionChannel,
  type RewardType,
} from '@sg/shared-fe-features-loyalty';

export type OrderingContext = Readonly<{
  cart: OrderingCart;
  rewards: readonly BagReward[];
  rewardError: string | undefined;
  isRewardMissingSku: boolean;
  availablePoints: number;
  appliedRewardId: string | undefined;
  paymentMethods: readonly PaymentMethod[];
  defaultPaymentMethodId: string | undefined;
  hasDefaultPaymentMethodCard: boolean;
  availableCredit: number;
  giftCardBalance: number;

  purchaseAttempt: number; // Used to remount the Stripe element after failures.
  stripeCustomerId: string;
  stripeCustomerKey: string;
  stripeSession: string;
  stripeConfirmHandlerRef: RefObject<NonNullable<IntentConfiguration['confirmHandler']>>; // prettier-ignore
  stripeElementConfig: EmbeddedPaymentElementConfiguration | null;
  stripeIntentConfig: IntentConfiguration | null;
  confirmStripe: () => void;

  hasTriggeredOrderSubmission: boolean; // to differentiate between post redirect and app reopen from regular flow.
  paymentMethodOrIntentId: string | undefined;

  customer: OrderingCustomer;

  wantedTime: WantedTime | undefined;
  previousWantedTime: WantedTime | undefined;
  isWantedTimeAsap: boolean;
  hasShownFutureDateWarning: boolean;
  isShowingOrderPlayback: boolean;

  hasShownUpsellModal: boolean;
  upsellItems: UpsellItem[];
  maxNumberOfUpsellProducts: number;
  placementVariation: UpsellPlacement;
  dropoffLocation: string | undefined;
  shouldUseCredit: boolean;
  shouldIncludeUtensils: boolean;

  selectedTip: number;
  availableTips: ReadonlyArray<number | 'CUSTOM'>;

  lineItemIdsBeingUpdated: string[];
  rewardIdBeingUpdated: string | undefined;
  rewardTermsBeingShown: string | undefined;

  orderId: string | undefined;
  hasEarnedLoyaltyPoints: boolean | undefined;

  orderSubmissionDelay: number;
  postOrderDelay: number;
  wantedTimesPollingInterval: number;
  bagCartPollingInterval: number;
}>;

export const initialContext: OrderingContext = {
  rewards: [],
  rewardError: undefined,
  isRewardMissingSku: false,
  appliedRewardId: undefined,
  paymentMethods: [],
  defaultPaymentMethodId: undefined,
  hasDefaultPaymentMethodCard: false,
  wantedTime: undefined,
  previousWantedTime: undefined,
  isWantedTimeAsap: true,
  hasShownFutureDateWarning: false,
  isShowingOrderPlayback: false,
  hasShownUpsellModal: false,
  upsellItems: [],
  maxNumberOfUpsellProducts: 6,
  placementVariation: 'control',
  dropoffLocation: undefined,
  shouldUseCredit: true,
  shouldIncludeUtensils: false,
  availablePoints: 0,
  availableCredit: 0,
  giftCardBalance: 0,
  selectedTip: 0,
  availableTips: [],
  lineItemIdsBeingUpdated: [],
  rewardIdBeingUpdated: undefined,
  rewardTermsBeingShown: undefined,
  orderId: undefined,
  hasEarnedLoyaltyPoints: undefined,

  purchaseAttempt: 0,
  stripeCustomerId: '',
  stripeCustomerKey: '',
  stripeSession: '',
  stripeConfirmHandlerRef:
    { current: () => Promise.resolve({ status: 'canceled', error: undefined }) }, // prettier-ignore
  stripeElementConfig: null,
  stripeIntentConfig: null,
  confirmStripe: () => Promise.resolve({ status: 'canceled', error: undefined }), // prettier-ignore

  hasTriggeredOrderSubmission: false,
  paymentMethodOrIntentId: undefined,

  orderSubmissionDelay: 0,
  postOrderDelay: 1500,
  wantedTimesPollingInterval: 0,
  bagCartPollingInterval: -1,

  cart: {
    orderId: undefined,
    orderChannel: 'pickup',
    ledger: undefined,
    locationName: undefined,
    restaurantId: undefined,
    restaurantEntityId: undefined,
    restaurantSlug: undefined,
    restaurantName: undefined,
    addressId: undefined,
    lineItems: [],
    wantedTimes: [],
    dropoffLocations: [],
    maxTipAmount: 0,
    deliveryFee: 0,
    deliveryOrderDetail: undefined,
    deliveryPreferences: { type: '', notes: '' },
    deliveryMinSubtotal: undefined,
    canTrackOrderStatus: false,
    appliedRewardId: undefined,
    showDeliveryFeeDisclosure: false,
    showDeliveryPriceDifferenciationDisclosure: false,
    showOutpostPriceDifferenciationDisclosure: false,
  },

  customer: {
    customerId: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    isNewCustomer: true,
    isOptedOutFromLoyalty: false,
  },
};
